import {
  BadgeCheck,
  BarChart2,
  Bike,
  CalendarDays,
  Clock3,
  Loader,
  LogIn,
  Mail,
  Phone,
} from "lucide-react";

const DriversReportsTab = ({ loadingDrivers, currentDrivers, onOpenAnalytics }) => {
  // =====================================================
  // DATE FORMATTER
  // =====================================================

  const formatDateTime = (value) => {
    if (!value) return "-";

    try {
      const date = new Date(value);

      return date.toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return value;
    }
  };

  // =====================================================
  // INFO BADGE
  // =====================================================

  const InfoBadge = ({ children, color = "gray" }) => {
    const styles = {
      gray: "bg-gray-100 text-gray-600",
      blue: "bg-blue-100 text-blue-700",
      green: "bg-green-100 text-green-700",
      orange: "bg-orange-100 text-orange-700",
      red: "bg-red-100 text-red-700",
      purple: "bg-purple-100 text-purple-700",
    };

    return (
      <span
        className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ${styles[color]}`}
      >
        {children}
      </span>
    );
  };

  return (
    <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
      <div className="overflow-x-auto">
        <table className="min-w-full">
          <thead className="border-b border-gray-200 bg-[#2EC8F1] text-white">
            <tr className="text-left">
              <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide">
                Driver
              </th>

              <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide">
                Contact
              </th>

              <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide">
                Vehicle / Shift
              </th>

              <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide">
                Team / Type
              </th>

              <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide">
                Status
              </th>

              <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide">
                Activity
              </th>

              <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wide">
                Actions
              </th>
            </tr>
          </thead>

          <tbody className="divide-y divide-gray-100 bg-white">
            {/* =====================================================
                LOADING
            ===================================================== */}

            {loadingDrivers ? (
              <tr>
                <td colSpan="7" className="px-4 py-8 text-center">
                  <div className="flex items-center justify-center gap-2">
                    <Loader className="h-5 w-5 animate-spin text-blue-600" />

                    <span className="text-gray-600">Loading drivers...</span>
                  </div>
                </td>
              </tr>
            ) : currentDrivers?.length === 0 ? (
              /* =====================================================
                  EMPTY
              ===================================================== */

              <tr>
                <td colSpan="7" className="px-4 py-8 text-center text-gray-500">
                  No drivers found
                </td>
              </tr>
            ) : (
              /* =====================================================
                  DRIVER ROWS
              ===================================================== */

              currentDrivers?.map((driver) => (
                <tr
                  key={driver.id || driver.driverId}
                  className="align-top transition-colors duration-200 hover:bg-orange-50/40"
                >
                  {/* =================================================
                      DRIVER
                  ================================================= */}

                  <td className="px-4 py-3">
                    <div className="flex min-w-0 items-center gap-3">
                      <img
                        src={driver.profile_photo || "/placeholder-driver.png"}
                        alt={driver.fullName || "Driver"}
                        onError={(e) => {
                          e.currentTarget.src =
                            "https://ui-avatars.com/api/?name=" +
                            encodeURIComponent(driver.fullName || "Driver") +
                            "&background=F3F4F6&color=374151";
                        }}
                        className="h-11 w-11 shrink-0 rounded-xl border border-gray-200 object-cover"
                      />

                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="truncate text-sm font-semibold leading-none text-gray-900">
                            {driver.fullName || "-"}
                          </p>

                          <InfoBadge color="blue">
                            {driver.driverId || "-"}
                          </InfoBadge>
                        </div>

                        <p className="mt-1 truncate text-xs text-gray-500">
                          @{driver.username || "-"}
                        </p>
                      </div>
                    </div>
                  </td>

                  {/* =================================================
                      CONTACT
                  ================================================= */}

                  <td className="px-4 py-3">
                    <div className="space-y-1 text-xs text-gray-700">
                      <div className="flex min-w-0 items-center gap-2">
                        <Phone size={13} className="shrink-0 text-gray-400" />

                        <span className="truncate">{driver.phone || "-"}</span>
                      </div>

                      <div className="flex min-w-0 items-center gap-2">
                        <Mail size={13} className="shrink-0 text-gray-400" />

                        <span className="truncate">{driver.email || "-"}</span>
                      </div>
                    </div>
                  </td>

                  {/* =================================================
                      VEHICLE / SHIFT
                  ================================================= */}

                  <td className="px-4 py-3">
                    <div className="space-y-1 text-xs text-gray-700">
                      <div className="flex min-w-0 items-center gap-2">
                        <Bike size={13} className="shrink-0 text-gray-400" />

                        <span className="truncate">
                          {driver.transport_type || "-"}

                          {driver.transport_description
                            ? ` • ${driver.transport_description}`
                            : ""}
                        </span>
                      </div>

                      <div className="flex min-w-0 items-center gap-2">
                        <BadgeCheck
                          size={13}
                          className="shrink-0 text-gray-400"
                        />

                        <span className="truncate">
                          {driver.licence_plate || "-"}
                        </span>
                      </div>

                      <div className="flex min-w-0 items-center gap-2">
                        <Clock3 size={13} className="shrink-0 text-gray-400" />

                        <span>
                          {driver.shift_start || "-"} -{" "}
                          {driver.shift_end || "-"}
                        </span>
                      </div>
                    </div>
                  </td>

                  {/* =================================================
                      TEAM / TYPE
                  ================================================= */}

                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1.5">
                      <InfoBadge color="orange">
                        {driver.teamName || "Unassigned"}
                      </InfoBadge>

                      <InfoBadge color="green">
                        {driver.driver_type || "N/A"}
                      </InfoBadge>

                      <InfoBadge color="purple">
                        {driver.tier || "N/A"}
                      </InfoBadge>
                    </div>
                  </td>

                  {/* =================================================
                      STATUS
                  ================================================= */}

                  <td className="px-4 py-3">
                    <div className="flex flex-col gap-2">
                      <div>
                        <InfoBadge
                          color={
                            driver.dutyStatus === "active" ? "green" : "gray"
                          }
                        >
                          {driver.dutyStatus === "active"
                            ? "On duty"
                            : "Off duty"}
                        </InfoBadge>
                      </div>

                      <div>
                        <InfoBadge
                          color={driver.status === "active" ? "green" : "red"}
                        >
                          {driver.status || "-"}
                        </InfoBadge>
                      </div>
                    </div>
                  </td>

                  {/* =================================================
                      ACTIVITY
                  ================================================= */}

                  <td className="px-4 py-3">
                    <div className="space-y-1 text-xs text-gray-700">
                      <div className="flex items-center gap-2">
                        <CalendarDays
                          size={13}
                          className="shrink-0 text-gray-400"
                        />

                        <span className="truncate">
                          {formatDateTime(driver.joiningDate)}
                        </span>
                      </div>

                      <div className="flex items-center gap-2">
                        <LogIn size={13} className="shrink-0 text-gray-400" />

                        <span className="truncate">
                          {formatDateTime(driver.last_login)}
                        </span>
                      </div>
                    </div>
                  </td>

                  {/* =================================================
                      ACTION
                  ================================================= */}

                  <td className="px-4 py-3">
                    <div className="flex items-center justify-center gap-1.5">
                      <button
                        type="button"
                        onClick={() => onOpenAnalytics(driver)}
                        className="inline-flex items-center gap-1 rounded-xl border border-gray-200 bg-white px-2.5 py-1.5 text-xs text-blue-600 transition hover:bg-blue-50"
                        title="Open Analytics"
                      >
                        <BarChart2 size={14} />
                        Analytics
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      
    </div>
  );
};

export default DriversReportsTab;
